import { CommonModule, registerLocaleData } from "@angular/common";
import {
  HTTP_INTERCEPTORS,
  HttpClient,
  provideHttpClient,
  withInterceptorsFromDi,
} from "@angular/common/http";
import { APP_INITIALIZER, LOCALE_ID, NgModule } from "@angular/core";

import { BrowserModule } from "@angular/platform-browser";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import {
  MissingTranslationHandler,
  MissingTranslationHandlerParams,
  TranslateLoader,
  TranslateModule,
  TranslateService,
} from "@ngx-translate/core";
import { AppRoutingModule } from "./app-routing.module";
import { AppComponent } from "./app.component";
import { AuthInterceptor } from "./core/interceptors/auth.interceptor";
import { LoggingInterceptor } from "./core/interceptors/logging.interceptor";
import { MultiTranslateHttpLoader } from "./core/loaders/multi-translate-http.loader";
import { FooterComponent } from "./shared/components/footer/footer.component";
import { HeaderComponent } from "./shared/components/header/header.component";
import { PageNotFoundComponent } from "./shared/components/page-not-found/page-not-found.component";
import { SharedModule } from "./shared/shared.module";

import { FormsModule } from "@angular/forms";
import { MainLayoutComponent } from "./shared/layouts/main-layout/main-layout.component";
import { SubLayoutComponent } from "./shared/layouts/sub-layout/sub-layout.component";
import { MatTooltipModule } from "@angular/material/tooltip";
import localeVi from "@angular/common/locales/vi";
import { MatButtonModule } from "@angular/material/button";
import { MatIconModule } from "@angular/material/icon";
import { MatMenuModule } from "@angular/material/menu";
import { LoadingComponent } from "./shared/components/loading/loading.component";
import { MatExpansionModule } from "@angular/material/expansion";

import { LoadingInterceptor } from "./shared/interceptor/loading.interceptor";
import { ErrorMessageService } from "./core/services/error-message.service";
import { RegistrationPolicyComponent } from './features/policy/pages/registration-policy/registration-policy.component';
import { InfoPrivacyPolicyComponent } from './features/policy/pages/info-privacy-policy/info-privacy-policy.component';
import { DisputeResolutionComponent } from './features/policy/pages/dispute-resolution/dispute-resolution.component';

registerLocaleData(localeVi);


export function createTranslateLoader(http: HttpClient) {
  return new MultiTranslateHttpLoader(http, "./assets/i18n/", ".json", [
    "auth",
    "common",
    "errors",
    "affiliate",
  ]);
}

export function initErrors(errorService: ErrorMessageService) {
  return async () => {
    const savedLang = localStorage.getItem("user_lang") as "vi" | "en" ?? "vi";
    await errorService.setLanguage(savedLang);
  };
}
export class CustomMissingTranslationHandler
  implements MissingTranslationHandler
{
  handle(params: MissingTranslationHandlerParams) {
    console.warn(
      `[i18n] Missing translation for key: '${params.key}' in '${params.translateService.currentLang}'`
    );
    return `{{${params.key}}}`;
  }
}

@NgModule({
  declarations: [
    AppComponent,
    PageNotFoundComponent,
    HeaderComponent,
    FooterComponent,
    MainLayoutComponent,
    SubLayoutComponent,
    LoadingComponent,
    RegistrationPolicyComponent,
    InfoPrivacyPolicyComponent,
    DisputeResolutionComponent,
  ],
  bootstrap: [AppComponent],
  imports: [
    CommonModule,
    BrowserModule,
    FormsModule,
    MatExpansionModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    SharedModule,
    MatIconModule,
    MatMenuModule,
    MatButtonModule,
    MatTooltipModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: createTranslateLoader,
        deps: [HttpClient],
      },
      missingTranslationHandler: {
        provide: MissingTranslationHandler,
        useClass: CustomMissingTranslationHandler,
      },
      isolate: false,
    })
],
  providers: [
    {
      provide: APP_INITIALIZER,
      useFactory: initErrors,
      deps: [ErrorMessageService],
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: LoggingInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: LoadingInterceptor,
      multi: true,
    },
    {
      provide: LOCALE_ID,
      useValue: "vi",
    },
    provideHttpClient(withInterceptorsFromDi()),
  ],
})
export class AppModule {
  constructor(private translate: TranslateService) {
    translate.addLangs(["en", "vi"]);
  }
}
